import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { customerSelector } from '@/api/v1';
import { useStepperStep } from '@/hooks/useStepperStep';
import { useTranslator } from '@/hooks/useTranslator';
import { useVerifyBusinessSteps } from '@/hooks/useVerifyBusinessSteps';
import { BusinessInfo } from '@/types/customer';
import { createFieldRenderer } from '@/utility/createFieldRenderer';
import { extractStepData } from '@/utility/extractStepData';

export const VerifyBusinessSummaryStep: React.FC = (): React.ReactElement => {
  const steps = useVerifyBusinessSteps();
  const navigate = useNavigate();
  const step = useStepperStep(steps);

  const { data: profile = { businessInfo: {} as BusinessInfo } } = useSelector(customerSelector);
  const { title, fields } = useStepperStep(steps);
  const { businessInfo } = profile;

  const tr = useTranslator();
  const data = React.useMemo(
    (): Record<string, unknown> => extractStepData(businessInfo, step),
    [businessInfo, step],
  );

  const goToStep = React.useCallback((): void => {
    const { parent } = step;
    navigate(['/verify-business', parent.path, step.path].join('/'), {
      state: {
        edit: true,
      },
      replace: true,
    });
  }, [navigate, step]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h6">{tr(title)}</Typography>
        <ButtonBase sx={styles.button} onClick={goToStep}>
          <Typography variant="body2" color="text.primary">
            {tr('EDIT')}
          </Typography>
        </ButtonBase>
      </Box>

      <Box sx={styles.entries}>{fields.map(createFieldRenderer(data))}</Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: 5,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'primary.contrastText',
    borderRadius: 'inherit',
    zIndex: 1,
    px: 5,
    pt: 6,
    pb: 2,
  },
  button: {
    height: 40,
    width: 80,
    borderRadius: 5,
    border: '1px solid',
    borderColor: 'custom.DividerLight',
  },
  entries: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 4,
    mt: 2,
    px: 5,
    pb: 5,
    mb: 3,
    overflowY: 'auto',
  },
};
